import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import Document from "../../models/document";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { BsFillPlusCircleFill } from "react-icons/bs";
import DocumentUpdateModal from "./DocumentUpdateModal";
import DocumentDeleteModal from "./DocumentDeleteModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import DocumentImportModal from "./DocumentImportModal";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  DOCUMENTS_CREATE,
  DOCUMENTS_DESTROY,
  DOCUMENTS_UPDATE,
} from "../../auth/permissions";

const ITEMS_PER_PAGE = 10;

const DocumentList = () => {
  const fields = [
    {
      key: "name",
      _classes: ["text-center"],
      label: "Nombre",
    },
    {
      key: "version",
      _classes: ["text-center"],
      label: "Versión",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [search, setSearch] = useState<string>("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [document, setDocument] = useState<Document | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchDocuments = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    urlParams.set("company_id", companyId);
    const documentsStatus = await getList<Document>(
      "/documents/",
      limit,
      offset,
      urlParams
    );
    if (documentsStatus.status === SUCCESS) {
      if (documentsStatus.data !== undefined) {
        const count = documentsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setDocuments(documentsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = documentsStatus.detail
        ? documentsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (document: Document) => {
    setDocument(document);
    setShowEditModal(true);
  };

  const onDeleteClick = (document: Document) => {
    setDocument(document);
    setShowDeleteModal(true);
  };

  const onDocumentEditCancel = () => {
    setDocument(undefined);
    setShowEditModal(false);
  };

  const onDocumentEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setDocuments([]);
    setLoading(true);
    fetchDocuments();
  };

  const onDocumentDeleteCancel = () => {
    setDocument(undefined);
    setShowDeleteModal(false);
  };

  const onDocumentDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setDocuments([]);
    setLoading(true);
    fetchDocuments();
  };

  const onImportClick = () => {
    setShowImportModal(true);
  };

  const onImportCancel = () => {
    setShowImportModal(false);
  };

  const onImportSuccess = () => {
    successAlert(
      "Importaciòn iniciada con exito. Podría demorarse unos minutos antes de ver las entradas en la lista!"
    );
    setShowImportModal(false);
    setDocuments([]);
    setLoading(true);
    fetchDocuments();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("documents")) {
        return;
      }
      fetchDocuments();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Documentos</h3>
              </div>
              <PermissionRequiredComponent permissionName={DOCUMENTS_CREATE}>
                <div className="p-2 ">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </div>
                <div className="p-2 ">
                  <CButton
                    color="success"
                    className="float-right"
                    onClick={onImportClick}
                  >
                    <i className="fa fa-file-excel" />
                    &nbsp; Importar XLSX
                  </CButton>
                </div>
              </PermissionRequiredComponent>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={documents}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    actions: (item: Document) => {
                      const editButton = (
                        <CButton
                          className="text-white"
                          color="warning"
                          onClick={() => {
                            onUpdateClick(item);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                      );
                      const deleteButton = (
                        <CButton
                          className="text-white"
                          color="danger"
                          onClick={() => {
                            onDeleteClick(item);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </CButton>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            <PermissionRequiredComponent
                              permissionName={DOCUMENTS_UPDATE}
                            >
                              {editButton}
                            </PermissionRequiredComponent>
                            <PermissionRequiredComponent
                              permissionName={DOCUMENTS_DESTROY}
                            >
                              {deleteButton}
                            </PermissionRequiredComponent>
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <DocumentUpdateModal
        show={showEditModal}
        onCancel={onDocumentEditCancel}
        onSuccess={onDocumentEditSuccess}
        document={document}
      />
      <DocumentDeleteModal
        show={showDeleteModal}
        onCancel={onDocumentDeleteCancel}
        onSuccess={onDocumentDeleteSuccess}
        document={document}
      />
      <DocumentImportModal
        show={showImportModal}
        onCancel={onImportCancel}
        onSuccess={onImportSuccess}
      ></DocumentImportModal>
    </>
  );
};

export default DocumentList;
